import React, { ChangeEvent, FunctionComponent, useMemo, useState } from "react";
import { ITranslatedNodes } from "../../../types/ITranslatedNodes";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import StarsIcon from "@material-ui/icons/Stars";
import Typography from "@material-ui/core/Typography";
import makeStyles from "@material-ui/core/styles/makeStyles";
import TranslatedText, {
  useTranslation,
} from "../TranslatedText/TranslatedText";
import UITranslations from "../../../store/uiTranslations";
import { grey, yellow } from "@material-ui/core/colors";
import styled from "styled-components";
import {
  LookupDistanceFunction,
} from "../../../store/DistanceMatrixStore";
import queryParamsStore from "../../../store/QueryParamsStore/QueryParamsStore";
import { observer, useLocalStore } from "mobx-react";
import buildingsStore from "../../../store/BuildingStore/BuildingStore";
import languagesStore from "../../../store/LanguagesStore/LanguagesStore";
import nodesStore from "../../../store/NodesStore/NodesStore";
import { getNodeIdFromPkidOrId } from "../../../utils/nodeDestinationUtils";
import { Button, ButtonBase, ClickAwayListener } from "@material-ui/core";
import { mobileCheck } from "../../../utils/isMobile";
import _ from 'lodash';
import { NodeTypes } from "../../../types/NodeTypes";
import { getTimeString } from "../../../utils/date";

interface INodeDropdown {
  placeholder: string;
  lookupDistance: LookupDistanceFunction;
  type: "to-node" | "from-node";
  value?: string;
  onChange: (v: string) => void;
}

const useStyles = makeStyles((theme) => ({
  autocomplete: {
    width: "100%",
    padding: "0px 10px",
  },
  autocompleteGroupLabel: {
    borderBottom: "2px solid #eee",
    lineHeight: "32px",
  },
}));

const NodeDropdown: FunctionComponent<INodeDropdown> = observer((props) => {
  const store = useLocalStore(() => ({
    isOpen: false,
  }));

  const isMobile = mobileCheck();

  const classes = useStyles();
  const frequentlyUsedI18N = useTranslation(
    UITranslations.PhotoRoutePage.FrequentlyUsed
  );

  const otherDestinationsI18N = useTranslation(
    UITranslations.PhotoRoutePage.OtherDestinations
  );
  const [inputValue, setInputValue] = useState("");
  const nodes = nodesStore.translatedNodes;

  let options = !nodes || nodes.length == 0 ? [] : nodes;

  const filterOptions = createFilterOptions({
    stringify: (option: ITranslatedNodes) =>
      `${getNodeName(option)}__${option.searchTerm}`,
  });
  const value = nodesStore.findNodeViaString(props.value!);
  const handleValueChange = (e, v: ITranslatedNodes) => {
    store.isOpen = false;
    props.onChange(v.pkid);
  };

  const nodeFrom = queryParamsStore.queryParams.nodeFrom;
  const folloupPkIds = useMemo(() => {
    if (props.type === "to-node" && nodeFrom) {
      const follupLocations = nodesStore.crossRefferences.find(
        (cr) => [cr.nodeId, cr.destinationId].join("-") === nodeFrom
      )?.followupLocation;

      if (follupLocations) {
        return follupLocations.map((fl) =>
          [fl.nodeId, fl.destinationId].join("-")
        );
      }
    }

    return [];
  }, [nodeFrom, nodesStore.crossRefferences]);
    
  options = options
    .filter(n => n.nodeTypeId !== NodeTypes.Junction)
    .map(option => {
      const distInPixel =
        props.type === "from-node"
          ? props.lookupDistance(
            option.id.toString(),
            getNodeIdFromPkidOrId(queryParamsStore.queryParams.nodeTo!)
          )
          : props.lookupDistance(
            getNodeIdFromPkidOrId(queryParamsStore.queryParams.nodeFrom!),
            option.id.toString()
          );

      const popular = folloupPkIds.includes(option.pkid) || option.popular;

      return { ...option, distInPixel, popular }
    })

  options = _.orderBy(options, ['popular', 'nodeTypeId', 'name', 'distInPixel'], ['desc', 'desc', 'asc', 'asc']);
  
  const inner = (
    <Autocomplete
      style={
        isMobile && !store.isOpen
          ? { pointerEvents: "none" }
          : { pointerEvents: "all" }
      }
      open={isMobile ? store.isOpen : undefined}
      onBlur={() => {
        store.isOpen = false;
      }}
      key={`${languagesStore.currentIETF}:${options.length}`}
      autoSelect={false}
      selectOnFocus={false}
      blurOnSelect="touch"
      openOnFocus
      options={options}
      renderInput={(params) => (
        <TextField {...params} placeholder={props.placeholder} fullWidth />
      )}
      getOptionLabel={option => {
        if (!option.closed) {
          return getNodeName(option);
        }

        const start = getTimeString(option.timerStart);
        const end = getTimeString(option.timerEnd);
        return `${getNodeName(option)} (Closed: ${start} To ${end})`
      }}
      groupBy={(opt) =>
        opt.popular ? frequentlyUsedI18N : otherDestinationsI18N
      }
      renderOption={(opt) => {
        return (
          <NodeOption
            distInPixel={opt.distInPixel}
            {...opt}
          />
        );
      }}
      filterOptions={filterOptions}
      value={value}
      onChange={handleValueChange as any}
      inputValue={inputValue}
      onInputChange={(e, v) => {
        setInputValue(v);
      }}
      disableClearable={true}
      classes={{
        root: classes.autocomplete,
        groupLabel: classes.autocompleteGroupLabel,
      }}
      getOptionDisabled={option => option.closed}
    />
  );

  // only wrap
  if (!isMobile) {
    return inner;
  }

  return (
    <ClickAwayListener
      onClickAway={() => {
        if (store.isOpen) {
          store.isOpen = false;
        }
      }}
    >
      <ButtonBase
        disableTouchRipple
        disabled={store.isOpen}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          store.isOpen = true;
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
        }}
        style={{
          width: "100%",
          ...(store.isOpen ? { pointerEvents: "none" } : undefined),
        }}
      >
        {inner}
      </ButtonBase>
    </ClickAwayListener>
  );
});

export default NodeDropdown;

const getNodeName = (node: ITranslatedNodes) => {
  return node.name || `Missing destination for node ${node.id}`;
};

const NodeOptionDiv = styled.div`
  display: flex;
  position: relative;
  padding-left: 1.25rem;
  width: 100%;

  .icon {
    position: absolute;
    left: -1rem;
    width: 1rem;
    height: 100%;
  }
`;

const NodeOption = observer(
  (
    props: ITranslatedNodes & {
      distInPixel: number | undefined
    }
  ) => {
    const { unitsPerPixel, default_unit } = buildingsStore.building!;
    const unitLabel = default_unit === "metrics" ? "m" : "ft";

    const finalDistText =
      props.distInPixel !== undefined && props.showDisplayDistance
        ? `${(props.distInPixel * unitsPerPixel).toFixed(0)} ${unitLabel}`
        : undefined;

    return (
      <NodeOptionDiv>
        <div className="icon">
          {props.popular && <StarsIcon style={{ color: yellow[600] }} />}
        </div>
        <Typography>
          {getNodeName(props)}{" "}
          {finalDistText !== undefined && `(${finalDistText})`}
        </Typography>
        {props.closed && (
          <Typography style={{ marginLeft: "auto" }}>
            Closed: {getTimeString(props.timerStart)} To&nbsp;
            {getTimeString(props.timerEnd)}
          </Typography>
        )}
      </NodeOptionDiv>
    );
  }
);
